import React, { useRef, useEffect } from 'react';
import { ExtendedDrugResult } from '../services/drugSearchService';

interface DrugSuggestionsDropdownProps {
  suggestions: ExtendedDrugResult[];
  selectedIndex: number;
  isLoading: boolean;
  searchSources: string[];
  query: string;
  position: { top: number; left: number; width: number };
  onSuggestionClick: (drug: ExtendedDrugResult) => void;
  onClose: () => void;
}

const SOURCE_BADGE_CONFIG: Record<string, { label: string; color: string }> = {
  'local': { label: '常用', color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  'inn': { label: 'INN', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  'inn_brand': { label: '品牌', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' },
  'inn_prefix': { label: 'INN', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  'matcher': { label: '智能匹配', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' },
  'fda': { label: 'FDA', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' },
  'ema': { label: 'EMA', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300' },
  'manual': { label: '手动输入', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200' },
  'external': { label: '外部', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' }
};

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase().trim();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <span className="font-semibold text-blue-600 dark:text-blue-400">
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
}

function getSourceBadge(source: string) {
  const badge = SOURCE_BADGE_CONFIG[source] || SOURCE_BADGE_CONFIG['external'];
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${badge.color}`}>
      {badge.label}
    </span>
  );
}

function getConfidenceIndicator(confidence: number) {
  if (confidence >= 0.95) return '🟢';
  if (confidence >= 0.8) return '🟡';
  if (confidence >= 0.6) return '🟠';
  return '⚪';
}

const DrugSuggestionsDropdown: React.FC<DrugSuggestionsDropdownProps> = ({
  suggestions,
  selectedIndex,
  isLoading,
  searchSources,
  query,
  position,
  onSuggestionClick,
  onClose
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (suggestions.length === 0) return null;

  return (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800
                 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl
                 max-h-96 overflow-y-auto animate-slideDown"
      style={{
        top: position.top,
        left: position.left,
        width: position.width
      }}
    >
      <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <span>
          找到 {suggestions.length} 个结果
          {searchSources.length > 0 && ` · 数据来源: ${searchSources.join(', ')}`}
        </span>
        {isLoading && (
          <span className="flex items-center gap-1">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            搜索中...
          </span>
        )}
      </div>

      {suggestions.map((drug, index) => (
        <div
          key={`${drug.id}-${index}`}
          onClick={() => onSuggestionClick(drug)}
          className={`px-4 py-3 cursor-pointer border-b border-gray-100 
                      dark:border-gray-700 last:border-b-0
                      transition-colors duration-150 ${
            index === selectedIndex
              ? 'bg-blue-50 dark:bg-blue-900/30'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {highlightMatch(drug.nameZh, query)}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {highlightMatch(drug.nameEn, query)}
                </span>
                <span title={`置信度: ${Math.round(drug.confidence * 100)}%`}>
                  {getConfidenceIndicator(drug.confidence)}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {getSourceBadge(drug.source)}
                {drug.category && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 
                                 text-gray-600 dark:text-gray-300 rounded">
                    {drug.category}
                  </span>
                )}
                {drug.therapeuticClass && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {drug.therapeuticClass}
                  </span>
                )}
              </div>
              {drug.inn && drug.inn !== drug.nameEn && (
                <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  INN: {drug.inn}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900/50">
        💡 提示：支持中文、英文、拼音、品牌名搜索 · 所有药品均经过INN标准化
      </div>
    </div>
  );
};

export default DrugSuggestionsDropdown;
